import { useCallback } from 'react';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';

/**
 * Mounts the dashboard's WebSocket subscription and renders sonner toasts
 * for every supported real-time event type. Keeps DashboardPage lean.
 *
 * @param {object} cb
 * @param {() => void} cb.onStatsRefresh - called for events that should trigger a stats reload
 * @param {(msg) => void} cb.onBudgetAlert - called when a budget_alert event arrives
 * @returns {{ wsConnected: boolean }}
 */
export const useDashboardRealtimeToasts = ({ onStatsRefresh, onBudgetAlert } = {}) => {
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'new_sale') {
      onStatsRefresh?.();
      const amount = Number(msg.amount || 0);
      if (amount > 0) {
        toast.success('New sale', {
          description: `${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${msg.actor || 'system'}`,
          duration: 4000,
        });
      }
    }
    if (msg.type === 'new_expense') {
      onStatsRefresh?.();
    }
    if (msg.type === 'match_confirmed') {
      const ent = msg.entity;
      const desc = ent ? `${ent.entity_type}: ${ent.name}` : `${msg.match_type || 'Match'}`;
      const amount = Number(msg.amount || 0);
      const auto = msg.auto ? '✨ auto-confirmed' : `confirmed by ${msg.actor || 'manager'}`;
      toast.success(`Bank match ${auto}`, {
        description: `${desc}${amount ? ` · ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}`,
        duration: 5000,
      });
    }
    if (msg.type === 'match_rejected') {
      toast.info('Bank match rejected', { description: `by ${msg.actor || 'manager'}`, duration: 4000 });
    }
    if (msg.type === 'payslip_disputed') {
      toast.warning('Payslip disputed', {
        description: `${msg.employee_name || 'employee'} · ${msg.month || ''}`.trim(),
        duration: 5000,
      });
    }
    if (msg.type === 'hr_doc_uploaded') {
      toast.success('HR document uploaded', {
        description: `${msg.doc_type || 'document'}${msg.employee_name ? ` · ${msg.employee_name}` : ''}`,
        duration: 4000,
      });
    }
    if (msg.type === 'journal_entry_created') {
      toast.info('Journal entry posted', {
        description: `${msg.reference || ''} · ${msg.actor || 'system'}`.trim(),
        duration: 3000,
      });
    }
    if (msg.type === 'leave_decided') {
      const tone = msg.decision === 'approved' ? toast.success : toast.info;
      tone(`Leave ${msg.decision}`, {
        description: `${msg.employee_name || 'employee'} · ${msg.days_count || '?'}d ${msg.leave_type || ''}`.trim(),
        duration: 4000,
      });
    }
    if (msg.type === 'expense_claim_decided') {
      const tone = msg.decision === 'approved' ? toast.success : toast.info;
      tone(`Expense claim ${msg.decision}`, {
        description: `${msg.employee_name || ''} · ${msg.currency || ''} ${Number(msg.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim(),
        duration: 4000,
      });
    }
    if (msg.type === 'payroll_run_completed') {
      onStatsRefresh?.();
      toast.success(`Payroll run · ${msg.period}`, {
        description: `${msg.employee_count} employees · net ${Number(msg.total_net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        duration: 5000,
      });
    }
    if (msg.type === 'budget_alert') {
      onBudgetAlert?.(msg);
      toast.warning('New budget alert triggered', { description: `${msg.data?.alerts_generated || 0} alert(s) generated` });
    }
    if (msg.type === 'low_stock_alert') {
      const count = msg.data?.count || 0;
      toast.warning(`Low Stock Alert: ${count} item(s)`, { description: msg.data?.items?.slice(0, 3).map(i => i.item_name).join(', ') || 'Check stock levels' });
    }
  }, [onStatsRefresh, onBudgetAlert]);

  const { isConnected: wsConnected } = useWebSocket(handleWsMessage);
  return { wsConnected };
};
